/* eslint-disable no-unused-vars */
import {ref, computed} from 'vue'
import type {SuggestionsRequestBody} from '~/types/ccrs'

export interface CcrsSearchItem {
  type: 'product' | 'design'
  id: string | number
  vendorName?: string
  vendorNameClean?: string
  competitorId?: number | null
  itemNumber?: string
  itemNumberClean?: string
  title?: string
  titleClean?: string
  designCategoryName?: string
  designCategoryNameClean?: string
  description?: string
  material?: string
  attributes?: any[]
  category?: string
  categoryClean?: string
}

export interface UseCcrsSearchOptions {
  postSuggestions: (payload: SuggestionsRequestBody, signal?: AbortSignal) => Promise<any>
  selectedCompetitorId: Ref<{id: number; name: string} | null>
  onSearchComplete?: (payload: Record<string, any>) => void,
  onSearch?: (payload: Record<string, any>) => void,
  locale?: string
}

export const useCcrsSearch = (options: UseCcrsSearchOptions) => {
  const {postSuggestions, selectedCompetitorId, onSearchComplete, onSearch, locale} = options

  // Search state
  const searchQuery = ref('')
  const isLoading = ref(false)
  const hasSearched = ref(false)
  const isLoadingMore = ref(false)
  const isLoadingMoreDesigns = ref(false)

  // Pagination
  const productTop = ref(50)
  const productSkip = ref(0)
  const designTop = ref(50)
  const designSkip = ref(0)

  // Results
  const itemNumbers = ref<CcrsSearchItem[]>([])
  const designs = ref<CcrsSearchItem[]>([])
  const productCount = ref(0)
  const designCount = ref(0)

  // Internal state
  let debounceTimer: ReturnType<typeof setTimeout> | null = null
  let lastSearchTerm = ''
  let pendingRequest: AbortController | null = null

  // Computed
  const flattenedResults = computed(() => [...itemNumbers.value, ...designs.value])

  // Map product from API response
  const mapProduct = (product: Record<string, any>): CcrsSearchItem => ({
    type: 'product',
    id: product.productSuggestionId,
    vendorName: product.competitor?.nameHighlighted || product.competitor?.name || '',
    vendorNameClean: product.competitor?.name || '',
    competitorId: product.competitor?.competitorId || null,
    itemNumber: product.skuHighlighted || product.sku,
    itemNumberClean: product.sku,
    title: product.design?.nameHighlighted || product.design?.name || '',
    titleClean: product.design?.name || '',
    designCategoryName: product.design?.categoryNameHighlighted || product.design?.categoryName || '',
    designCategoryNameClean: product.design?.categoryName || '',
    description: product.design?.technicalName || '',
    material: product.material?.compound || '',
    attributes: product.attributes || []
  })

  // Map design from API response
  const mapDesign = (design: Record<string, any>): CcrsSearchItem => ({
    type: 'design',
    id: design.designSuggestionId,
    title: design.nameHighlighted || design.longName || design.name || '',
    titleClean: design.longName || design.name || '',
    description: [design.technicalName, design.name].filter(Boolean).join(' - '),
    category: design.categoryNameHighlighted || design.categoryName,
    categoryClean: design.categoryName,
    competitorId: design.competitor?.competitorId || null,
    vendorName: design.competitor?.nameHighlighted || design.competitor?.name || ''
  })

  // Create suggestions payload
  const createSuggestionsPayload = (): SuggestionsRequestBody => ({
    searchTerm: searchQuery.value,
    productTop: productTop.value,
    productSkip: productSkip.value,
    designTop: designTop.value,
    designSkip: designSkip.value,
    competitorId: selectedCompetitorId.value?.id ?? null
  })

  // Perform search
  const performSearch = async (term: string) => {
    if (term.length === 0) {
      clearResults()
      return
    }

    pendingRequest?.abort()
    const currentRequest = new AbortController()
    pendingRequest = currentRequest

    lastSearchTerm = term
    productSkip.value = 0
    designSkip.value = 0
    isLoading.value = true
    hasSearched.value = true

    try {
      const response = await postSuggestions(createSuggestionsPayload(), currentRequest.signal)

      if (response) {
        productCount.value = response.productsTotalCount || 0
        designCount.value = response.designsTotalCount || 0

        itemNumbers.value = response.products?.length > 0
          ? response.products.map(mapProduct)
          : []

        designs.value = response.designs?.length > 0
          ? response.designs.map(mapDesign)
          : []

        onSearchComplete?.({
          searchTerm: term,
          productsCount: productCount.value,
          designsCount: designCount.value,
          competitorId: selectedCompetitorId.value?.id || '',
          competitorName: selectedCompetitorId.value?.name || '',
          language: locale || ''
        })
      }
    } catch (error: any) {
      if (error?.name !== 'AbortError') {
        itemNumbers.value = []
        designs.value = []
      }
    } finally {
      if (pendingRequest === currentRequest) {
        isLoading.value = false
      }
    }
  }

  // Trigger search with debounce
  const triggerSearch = (isOpen: Ref<boolean>) => {
    if (debounceTimer) {
      clearTimeout(debounceTimer)
    }

    if (searchQuery.value.length === 0) {
      clearResults()
      isOpen.value = false
      return
    }

    isOpen.value = true

    if (searchQuery.value === lastSearchTerm && flattenedResults.value.length > 0) {
      return
    }

    debounceTimer = setTimeout(() => {
      performSearch(searchQuery.value)
    }, 300)
  }

  // Load more products
  const loadMoreProducts = async (dropdownRef: Ref<HTMLElement | null>) => {
    if (isLoadingMore.value || itemNumbers.value.length >= productCount.value) {
      return
    }

    const firstNewItemIndex = itemNumbers.value.length
    isLoadingMore.value = true

    if (dropdownRef.value) {
      dropdownRef.value.style.overflowY = 'hidden'
    }

    productSkip.value += productTop.value

    try {
      const response = await postSuggestions(createSuggestionsPayload(), pendingRequest?.signal)

      if (response?.products?.length > 0) {
        itemNumbers.value = [...itemNumbers.value, ...response.products.map(mapProduct)]

        await nextTick()
        if (dropdownRef.value) {
          dropdownRef.value.style.overflowY = 'auto'
        }

        await nextTick()
        if (dropdownRef.value) {
          const items = dropdownRef.value.querySelectorAll('.section__items .item:not(.item--skeleton)')
          if (items[firstNewItemIndex]) {
            const firstNewItem = items[firstNewItemIndex] as HTMLElement
            dropdownRef.value.scrollTop = firstNewItem.offsetTop - 80
          }
        }
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[CCRS] Error loading more products:', error)
    } finally {
      isLoadingMore.value = false
      if (dropdownRef.value) {
        dropdownRef.value.style.overflowY = 'auto'
      }
    }
  }

  // Load more designs
  const loadMoreDesigns = async () => {
    if (isLoadingMoreDesigns.value || designs.value.length >= designCount.value) {
      return
    }

    isLoadingMoreDesigns.value = true
    designSkip.value += designTop.value

    try {
      const response = await postSuggestions(createSuggestionsPayload(), pendingRequest?.signal)

      if (response?.designs?.length > 0) {
        designs.value = [...designs.value, ...response.designs.map(mapDesign)]
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[CCRS] Error loading more designs:', error)
    } finally {
      isLoadingMoreDesigns.value = false
    }
  }

  // Clear results
  const clearResults = () => {
    itemNumbers.value = []
    designs.value = []
    productCount.value = 0
    designCount.value = 0
    productSkip.value = 0
    designSkip.value = 0
    hasSearched.value = false
    lastSearchTerm = ''
  }

  // Clear search
  const clearSearch = () => {
    pendingRequest?.abort()
    pendingRequest = null
    searchQuery.value = ''
    clearResults()
  }

  // Reset pagination
  const resetPagination = () => {
    productSkip.value = 0
    designSkip.value = 0
  }

  // Check if has results for current term
  const hasResultsForCurrentTerm = () => {
    return searchQuery.value === lastSearchTerm && flattenedResults.value.length > 0
  }

  return {
    // State
    searchQuery,
    isLoading,
    hasSearched,
    isLoadingMore,
    isLoadingMoreDesigns,
    itemNumbers,
    designs,
    productCount,
    designCount,
    flattenedResults,

    // Methods
    triggerSearch,
    performSearch,
    loadMoreProducts,
    loadMoreDesigns,
    clearSearch,
    clearResults,
    resetPagination,
    hasResultsForCurrentTerm
  }
}

export type UseCcrsSearch = ReturnType<typeof useCcrsSearch>
